if (!customElements.get('subscription-selector')) {
  customElements.define(
    'subscription-selector',
    class SubscriptionSelector extends HTMLElement {
      constructor() {
        super();
        this.syncSellingPlan = this.syncSellingPlan.bind(this);
      }

      connectedCallback() {
        this.addEventListener('change', this.syncSellingPlan);
        this.syncSellingPlan();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.syncSellingPlan);
      }

      syncSellingPlan() {
        const selectedOption = this.querySelector('input[name="selling_plan"]:checked');
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        productInfo.querySelectorAll('[data-subscription-selling-plan]').forEach((sellingPlanInput) => {
          sellingPlanInput.value = selectedOption?.value || '';
          sellingPlanInput.disabled = !selectedOption?.value;
        });

        this.updateDisplayedPrices(productInfo, selectedOption);
      }

      updateDisplayedPrices(productInfo, selectedOption) {
        if (!selectedOption?.hasAttribute('data-purchase-option-price')) return;

        const priceText = selectedOption.dataset.priceText;
        const compareAtPriceText = selectedOption.dataset.compareAtPriceText;
        const priceValue = Number(selectedOption.dataset.price);
        const compareAtPriceValue = Number(selectedOption.dataset.compareAtPrice);
        const isOnSale = compareAtPriceValue > priceValue;

        productInfo.querySelectorAll('[data-sticky-product-price]').forEach((price) => {
          price.textContent = priceText;
        });

        const priceElement = productInfo.querySelector('.product__info-container [id^="price-"] .price');
        if (!priceElement) return;

        const regularPriceItems = priceElement.querySelectorAll('.price__regular .price-item--regular');
        const regularPrice = regularPriceItems[regularPriceItems.length - 1];
        const saleCompareAtPrice = priceElement.querySelector('.price__sale .price-item--regular');
        const salePrice = priceElement.querySelector('.price__sale .price-item--sale');

        if (regularPrice) regularPrice.textContent = priceText;
        if (saleCompareAtPrice) saleCompareAtPrice.textContent = compareAtPriceText;
        if (salePrice) salePrice.textContent = priceText;

        priceElement.classList.toggle('price--on-sale', isOnSale);
      }
    }
  );
}

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (this.quantityInputs.length && this.quantityForm) {
          this.setQuantityBoundries();
          this.multiplayDosage();
        }

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      runNumberAnimation() {
        this.quantityInputs.forEach((input) => {
          const dynamicClass = input.dataset?.dynamicClass
          
          if (dynamicClass) {
            input.classList.add(dynamicClass);
            const animTimeout = setTimeout(() => {
              input.classList.remove(dynamicClass);
              clearTimeout(animTimeout);
            }, 200);
          }
        })
      }

      multiplayDosage() {
        this.quantityInputs.forEach((input) => {          
          input.addEventListener('change', (e) => {
            this.runNumberAnimation()
            this.querySelectorAll('[data-base-amount-to-multiplay]').forEach((element) => {
              const dosage = element.dataset.baseAmountToMultiplay;
              if(dosage) element.textContent = (Number(e.target.value) * Number(dosage));
            });
          });
        })
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          const stickyAddToCart = html.querySelector('sticky-buy-button');
          const oldStickyAddToCart = this.querySelector('sticky-buy-button');
          const buyButtons = html.querySelectorAll('.buy-buttons-block');
          const oldBuyButtons = this.querySelectorAll('.buy-buttons-block');
          const deliveryIcon = html.querySelector('[data-icon-delivery-time]');
          const oldDeliveryIcon = this.querySelector('[data-icon-delivery-time]');

          if (deliveryIcon && oldDeliveryIcon) oldDeliveryIcon.textContent = deliveryIcon.textContent;
          if (stickyAddToCart && oldStickyAddToCart) oldStickyAddToCart.innerHTML = stickyAddToCart.innerHTML;
          if (buyButtons && oldBuyButtons) {
            oldBuyButtons.forEach((oldButton, index) => {
              oldButton.innerHTML = buyButtons[index].innerHTML;
            });
          }

          this.multiplayDosage();

          this.pickupAvailability?.update?.(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          this.updateMedia(html, variant?.featured_media?.id); 

          if (!variant) {
            this.setUnavailable();
            return;
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          this.updateSubscriptionOptions(html);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateSubscriptionOptions(html) {
        const source = html.getElementById(`Subscription-${this.sectionId}`);
        const destination = this.querySelector(`#Subscription-${this.dataset.section}`);
        if (!source || !destination) return;

        const selectedSellingPlan = destination.querySelector('input[name="selling_plan"]:checked')?.value;
        destination.innerHTML = source.innerHTML;
        destination.classList.toggle('hidden', source.classList.contains('hidden'));

        if (selectedSellingPlan) {
          const matchingOption = Array.from(destination.querySelectorAll('input[name="selling_plan"]')).find(
            (option) => option.value === selectedSellingPlan
          );

          if (matchingOption) matchingOption.checked = true;
        }

        destination.querySelector('subscription-selector')?.syncSellingPlan();
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules', 'Subscription']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const gallery = this.querySelector('.media-gallery')
        if(!gallery) return;

        const mediaSize = gallery.dataset.items;
        const newVariantPhoto = gallery.querySelector(`[data-media-id="${this.dataset.productId}-${variantFeaturedMediaId}"]`);

        if (mediaSize === '2') {
          const otherImage = Array.from(gallery.querySelectorAll('div[data-media-id]')).find((el) => el.dataset.mediaId != `${this.dataset.productId}-${variantFeaturedMediaId}`);
          if(newVariantPhoto && otherImage) otherImage.parentElement.insertBefore(newVariantPhoto, otherImage);

        } else if (mediaSize > 2) {
          const swiper = gallery.querySelector('swiper-container');
          if(newVariantPhoto && swiper) swiper.swiper.slideTo([...swiper.children].indexOf(newVariantPhoto))
        }
      }

      setQuantityBoundries() {
        const firstQaInput = this.quantityInputs[0];
        if (!firstQaInput) return;

        const data = {
          cartQuantity: firstQaInput.dataset.cartQuantity ? parseInt(firstQaInput.dataset.cartQuantity) : 0,
          min: firstQaInput.dataset.min ? parseInt(firstQaInput.dataset.min) : 1,
          max: firstQaInput.dataset.max ? parseInt(firstQaInput.dataset.max) : null,
          step: firstQaInput.step ? parseInt(firstQaInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInputs.forEach((input) => {
          input.min = min;
          if (max) {
            input.max = max;
          } else {
            input.removeAttribute('max');
          }
          input.value = min;
        })

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        const quantityForm = this.quantityForm;
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!this.quantityInputs.length || !quantityForm || !quantityFormUpdated) return;

        this.setQuantityBoundries();

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get quantityInputs() {
        return this.querySelectorAll('.quantity__input');
      }

      get quantityForm() {
        return this.querySelector('.product-form__quantity');
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
